/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { isFormulaEmpty } from '@gh/builder-shared-data';
import {
  conditionalCellRenderer,
  createDefaultGridModel,
  falsyCellRenderer,
  GREEN_MARKER_COLOR,
  notEmptyCellRenderer,
  rendererColorCircle,
  rendererIconByName,
  ROW_DELETED_CLASS,
  ROW_DIRTY_CLASS,
} from '@gh/common-internal-shared';
import { NumberTypes, State } from '@gh/common-shared';
import {
  buildEditableNumberColumn,
  buildNumberColumn,
  gridCollectionComparator,
  GridModel,
  GridOptionsExt,
  LayoutBasedGridDefinitionWithModel,
} from '@gh/core-ui-grid';
import { ColDef } from 'ag-grid-community';
import { get, isNil, negate } from 'lodash';
import {
  getHpeComponentId,
  HpeComponent,
  hpeComponentFieldState,
  validateHpeComponentField,
} from '../../../resources/hpe-details.model';
import { HpeCommandService } from '../hpe-command.service';
import { hpeComponentComparator, HpeDetailsState, queryHpeEditable, queryHpeUomRefItem } from '../hpe-details.state';

@Injectable()
export class HpeDetailsComponentGridDefinition extends LayoutBasedGridDefinitionWithModel {
  constructor(private state: State<HpeDetailsState>,
              private commands: HpeCommandService) {
    super();
  }

  buildGridModel(): GridModel {
    return createDefaultGridModel<HpeComponent>({
      editable: this.state.project(queryHpeEditable),
      getId: getHpeComponentId,
      validate: validateHpeComponentField,
      getCellDef: hpeComponentFieldState,
      command: (entity, name, value) => this.commands.componentSetField(getHpeComponentId(entity), name, value),
    });
  }

  buildGridOptions(): GridOptionsExt {
    return {
      rowSelection: 'single',
      defaultColDef: {
        sortable: false,
      },
      rowClassRules: {
        [ROW_DELETED_CLASS]: ({ data }: { data: HpeComponent }) => data.isActive !== true,
        [ROW_DIRTY_CLASS]: ({ data }: { data: HpeComponent }) => !isNil(data.dirtyType),
      },
    };
  }

  buildColumnDefs(): Hash<ColDef> {
    return {
      cost: buildNumberColumn(NumberTypes.Price),
      extension: buildNumberColumn(NumberTypes.Price),
      code: {
        sort: 'asc',
        comparator: gridCollectionComparator(hpeComponentComparator),
      },
      quantity: buildEditableNumberColumn(NumberTypes.SignedQuantity, this.model.getCellAccessor('quantity')),
      uomCode: {
        valueFormatter: ({ data }: { data: any }) =>
          get(this.state.project(queryHpeUomRefItem, data.uomId), 'code', ''),
      },
      isCut: {
        field: 'cuttingList',
        cellRenderer: notEmptyCellRenderer(rendererColorCircle(GREEN_MARKER_COLOR)),
      },
      isFormula: {
        field: 'formula',
        cellRenderer: conditionalCellRenderer(negate(isFormulaEmpty), rendererColorCircle(GREEN_MARKER_COLOR)),
      },
      isActive: {
        cellRenderer: falsyCellRenderer(rendererIconByName('done')),
      },
    };
  }
}
